import { useRef, useState } from 'react';
import { Loader2, Upload, FileText, ExternalLink, CheckCircle2, Clock, XCircle } from 'lucide-react';
import { useGetFilteredDocumentsQuery, useUploadAgentDocumentMutation } from '@/store/api/apiSlice';
import { resolveFileUrl } from '@/utils/fileUrl';
import { validateInvestigationFile } from '@/utils/fileValidation';
import type { InvestigationDocument } from '@/types';

// The agent uploads a Social Media report (PDF / Word / image) as part of the
// investigation. Only shown for plans whose scope includes a social-media review
// — the parent gates on that before rendering this panel. Uploaded files land in
// the admin approval queue (awaiting_approval).
const SOCIAL_MEDIA_REPORT_TYPE = 'social_media_report';

interface Props {
    investigationId: string;
    canEdit: boolean;
}

function StatusBadge({ status }: { status?: string | undefined }) {
    if (status === 'approved' || status === 'completed') {
        return (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-md">
                <CheckCircle2 className="w-3 h-3" /> Approved
            </span>
        );
    }
    if (status === 'rejected') {
        return (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-red-700 bg-red-50 px-2 py-0.5 rounded-md">
                <XCircle className="w-3 h-3" /> Rejected
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[#FF6B35] bg-[#FF6B35]/10 px-2 py-0.5 rounded-md">
            <Clock className="w-3 h-3" /> Awaiting approval
        </span>
    );
}

export default function SocialMediaReportPanel({ investigationId, canEdit }: Props) {
    const { data: agentDocs } = useGetFilteredDocumentsQuery(
        { id: investigationId, uploadedBy: 'agent' },
        { skip: !investigationId },
    );
    const [uploadAgentDoc, { isLoading: isUploading }] = useUploadAgentDocumentMutation();
    const [error, setError] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    // `documentType` is typed to the DocumentType enum, but the backend also emits
    // 'social_media_report' (not in that enum yet), so compare as a plain string.
    const reports: InvestigationDocument[] = (agentDocs ?? []).filter(
        (d) => String(d.documentType) === SOCIAL_MEDIA_REPORT_TYPE,
    );

    const handleSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        // 10 MB + PDF/DOC/DOCX/image — matches the server-enforced rules.
        const validationError = validateInvestigationFile(file);
        if (validationError) {
            setError(validationError);
            return;
        }
        setError('');
        try {
            await uploadAgentDoc({
                id: investigationId,
                file,
                documentType: SOCIAL_MEDIA_REPORT_TYPE,
            }).unwrap();
        } catch (err: any) {
            setError(err?.data?.message || 'Failed to upload the social media report. Please try again.');
        }
    };

    const openDoc = (doc: InvestigationDocument) => {
        const url = doc.downloadUrl || doc.fileUrl;
        if (url) window.open(resolveFileUrl(url), '_blank', 'noopener,noreferrer');
    };

    return (
        <div className="bg-white border border-[#e0e0e0] rounded-xl p-5">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                <div>
                    <h3 className="text-sm font-bold text-[#1b1b1b]">Social media report</h3>
                    <p className="text-xs text-[#4e4d4d] mt-0.5">
                        Attach the social media findings for this investigation. PDF or Word (.docx) is preferred, up to
                        10 MB. It goes to a super admin for approval.
                    </p>
                </div>
                {canEdit && (
                    <>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf,.doc,.docx,image/jpeg,image/png"
                            onChange={handleSelect}
                            className="hidden"
                        />
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isUploading}
                            className="bg-[#FF6B35] hover:bg-[#FF5722] disabled:opacity-60 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 shrink-0"
                        >
                            {isUploading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Upload className="w-4 h-4" />
                            )}
                            {isUploading ? 'Uploading' : reports.length > 0 ? 'Upload another' : 'Upload report'}
                        </button>
                    </>
                )}
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700 mb-3">
                    {error}
                </div>
            )}

            {reports.length === 0 ? (
                <div className="bg-[#fafafa] border border-dashed border-[#e0e0e0] rounded-lg py-6 text-center">
                    <FileText className="w-5 h-5 text-[#4e4d4d] mx-auto mb-1.5" />
                    <p className="text-xs text-[#4e4d4d]">
                        {canEdit
                            ? 'No social media report uploaded yet.'
                            : 'The agent has not uploaded a social media report yet.'}
                    </p>
                </div>
            ) : (
                <ul className="flex flex-col gap-2">
                    {reports.map((doc) => (
                        <li
                            key={doc.id}
                            className="flex items-center gap-3 border border-[#e0e0e0] rounded-lg px-3 py-2.5"
                        >
                            <div className="w-9 h-9 rounded-lg bg-[#FF6B35]/10 flex items-center justify-center shrink-0">
                                <FileText className="w-4 h-4 text-[#FF6B35]" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-[#1b1b1b] truncate">{doc.fileName}</p>
                                <div className="mt-0.5">
                                    <StatusBadge status={doc.approvalStatus} />
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => openDoc(doc)}
                                className="inline-flex items-center gap-1 text-xs text-[#1b1b1b] border border-[#e0e0e0] hover:bg-gray-50 px-2.5 py-1.5 rounded-lg shrink-0"
                            >
                                <ExternalLink className="w-3.5 h-3.5" />
                                {doc.mimeType === 'application/pdf' ? 'View' : 'Download'}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
